import { ChevronRight, Check } from "lucide-react";
import { cn } from "~/lib/utils";

interface Props {
  currentIndex: number;
  steps: {
    title: string | null;
    description?: string | null;
  }[];
  /** "sm" is a single compact row for dialogs; step descriptions are not shown. */
  size?: "default" | "sm";
}

export const StepProgress = ({
  currentIndex,
  steps,
  size = "default",
}: Props) => {
  const compact = size === "sm";

  return (
    <div
      className={cn(
        "flex flex-wrap items-center",
        compact ? "gap-x-2 gap-y-1" : "justify-center gap-y-4",
      )}
    >
      {steps.map((step, index) => {
        if (!step.title) return null;

        const isComplete = currentIndex > index;
        const isActive = currentIndex === index;
        const hasNext = Boolean(steps[index + 1]?.title);

        const indicator = (
          <div
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full",
              compact ? "size-6 text-xs font-medium" : "size-10",
              {
                "bg-success text-primary-foreground": isComplete,
                "bg-info text-primary-foreground": !isComplete && isActive,
                "bg-muted text-muted-foreground": !isComplete && !isActive,
              },
            )}
          >
            {isComplete ? (
              <Check className={compact ? "size-3.5" : "size-5"} />
            ) : (
              index + 1
            )}
          </div>
        );

        return (
          <div
            key={index}
            className={cn("flex items-center", compact && "gap-2")}
            aria-current={isActive ? "step" : undefined}
          >
            {compact ? (
              <div className="flex items-center gap-1.5">
                {indicator}
                <span
                  className={cn("text-sm", {
                    "font-medium": isActive,
                    "text-muted-foreground": !isComplete && !isActive,
                  })}
                >
                  {step.title}
                </span>
              </div>
            ) : (
              <div className="flex h-28 w-52 flex-col items-center gap-2">
                {indicator}
                <div className="text-center text-sm">
                  <div className="font-medium">{step.title}</div>
                  <div className="text-muted-foreground text-xs">
                    {step.description}
                  </div>
                </div>
              </div>
            )}
            {hasNext && (
              <ChevronRight
                className={cn(
                  "text-muted-foreground",
                  compact ? "size-4" : "size-5",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
